import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { generateMockCostBreakdown } from '../mockShipmentData';
import { ifsService } from '../../../../services/ifsService';
import { featureFlagService } from '../../../../services/featureFlagService';
import { shipmentService } from '../../../../services/shipmentService';
import { getActiveIFSAccountId } from '../../../../utils/ifsAccount';
import { DetailGrid, DetailSection, DetailRow, CostTable, TotalAmount, ActionButton } from '../IFSShippingDashboard.styles';
const CostPreviewStep = ({ isMockMode, sender, recipient, pkg, cost, onCostCalculated }) => {
    const [loading, setLoading] = useState(!cost);
    const [error, setError] = useState(null);
    const [labelEnabled, setLabelEnabled] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [labelResult, setLabelResult] = useState(null);
    useEffect(() => {
        if (isMockMode)
            return;
        featureFlagService.get('ifs_label_creation')
            .then(res => setLabelEnabled(!!(res.ok && res.data?.success && res.data.enabled)))
            .catch(() => setLabelEnabled(false));
    }, [isMockMode]);
    const handleGenerateLabel = async () => {
        const estimate = cost?.finalAmount || 'unknown cost';
        if (!window.confirm(`Generate a REAL FedEx label via IFS?\n\nEstimated cost: ${estimate}\nShip date: ${pkg.shipDate}\n\nThis will be billed to the IFS account.`))
            return;
        setGenerating(true);
        setLabelResult(null);
        try {
            const res = await featureFlagService.createLabel({
                accountId: getActiveIFSAccountId(),
                sender,
                recipient,
                pkg,
                cost,
                options: {
                    shipDate: pkg.shipDate || '',
                    signatureType: pkg.signatureType,
                    paymentType: pkg.paymentType || 'SENDER',
                    accountNumber: pkg.accountNumber,
                    reference: pkg.reference,
                    referenceShowOnLabel: pkg.referenceShowOnLabel,
                    labelFormat: pkg.labelFormat,
                    saturdayDelivery: pkg.saturdayDelivery,
                    estimatedCost: (cost?.finalAmount || '').replace(/[^0-9.]/g, ''),
                    ...{ holdForPickup: pkg.holdForPickup, displayReceipt: pkg.displayReceipt,
                        schedulePickup: pkg.schedulePickup, pickupDate: pkg.pickupDate,
                        pickupTime: pkg.pickupTime, pickupTimeLatest: pkg.pickupTimeLatest,
                        pickupInstructions: pkg.pickupInstructions },
                },
            });
            if (res.ok && res.data?.success && res.data.tracking_no) {
                const tracking = res.data.tracking_no;
                setLabelResult({ ok: true, msg: res.data.message || 'Label generated.', tracking });
                // Persist the shipment with its tracking number
                await shipmentService.create({
                    status: 'label_created',
                    source: 'wizard',
                    trackingNumber: tracking,
                    shipmentId: res.data.shipment_id ? String(res.data.shipment_id) : null,
                    sender, recipient, package: pkg,
                    cost: cost || { finalAmount: '', breakdown: [], zoneId: '', calculatedAt: new Date().toISOString() },
                    delivery: { deliveredTo: null, deliveredDate: null, deliveredSignature: null, fedexStatus: null },
                    documents: { labelLink: null, returnLabelLink: null, commercialInvoiceLink: null, receiptLink: null },
                });
                // Open the label PDF (thermal labels come from a different endpoint)
                const isThermal = res.data.is_thermal_label === 'Yes';
                const pdfRes = isThermal
                    ? await ifsService.getThermalLabelPDF({ tracking_no: tracking })
                    : await ifsService.getLabelPDF({ tracking_no: tracking });
                const b64 = pdfRes.data?.data?.pdf_base64;
                if (pdfRes.ok && pdfRes.data?.success && b64) {
                    const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
                    window.open(URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' })), '_blank', 'noopener');
                }
            }
            else {
                setLabelResult({ ok: false, msg: res.data?.message || res.data?.error || res.error || 'Label creation failed' });
            }
        }
        catch (e) {
            setLabelResult({ ok: false, msg: e.message });
        }
        finally {
            setGenerating(false);
        }
    };
    useEffect(() => {
        if (cost)
            return;
        calculateCost();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const calculateCost = async () => {
        setLoading(true);
        setError(null);
        try {
            if (isMockMode) {
                await new Promise(r => setTimeout(r, 800));
                const mockCost = generateMockCostBreakdown(pkg.serviceType, pkg.weight, pkg.declaredValue);
                onCostCalculated(mockCost);
            }
            else {
                const res = await ifsService.calculateCost({
                    sender_address_id: sender.addressId || '',
                    recipient_zip: recipient.zip,
                    recipient_country: recipient.country,
                    recipient_state: recipient.state,
                    recipient_residential: recipient.isResidential ? '1' : '0',
                    packaging_type: pkg.packagingType,
                    service_type: pkg.serviceType,
                    package_weight: String(pkg.weight),
                    declare_value: String(pkg.declaredValue),
                });
                if (res.ok && res.data?.success) {
                    const costData = res.data?.data;
                    const calculated = {
                        finalAmount: costData.final_amount || '',
                        breakdown: (costData.cost_breakdown || []).map((c) => ({ title: c.text || c.title, displayValue: c.value || c.displayValue, messageType: c.message_type || '' })),
                        zoneId: costData.zone_id || '',
                        calculatedAt: new Date().toISOString(),
                    };
                    onCostCalculated(calculated);
                }
                else {
                    setError(res.error || 'Cost calculation failed');
                }
            }
        }
        catch (err) {
            setError(err.message || 'Failed to calculate cost');
        }
        finally {
            setLoading(false);
        }
    };
    return (_jsxs("div", { children: [_jsx("h3", { style: { color: 'var(--text-primary)', marginBottom: '1.25rem' }, children: "Review & Save" }), _jsxs(DetailGrid, { children: [_jsxs(DetailSection, { children: [_jsx("h4", { children: "Sender" }), _jsxs(DetailRow, { children: [_jsx("span", { className: "label", children: "Name" }), _jsx("span", { className: "value", children: sender.name })] }), _jsxs(DetailRow, { children: [_jsx("span", { className: "label", children: "Address" }), _jsxs("span", { className: "value", children: [sender.address1, ", ", sender.city, ", ", sender.state, " ", sender.zip] })] })] }), _jsxs(DetailSection, { children: [_jsx("h4", { children: "Recipient" }), _jsxs(DetailRow, { children: [_jsx("span", { className: "label", children: "Name" }), _jsx("span", { className: "value", children: recipient.name })] }), _jsxs(DetailRow, { children: [_jsx("span", { className: "label", children: "Address" }), _jsxs("span", { className: "value", children: [recipient.address1, ", ", recipient.city, ", ", recipient.state, " ", recipient.zip] })] })] })] }), _jsxs(DetailSection, { style: { marginTop: '1.5rem' }, children: [_jsx("h4", { children: "Package" }), _jsxs(DetailGrid, { children: [_jsxs("div", { children: [_jsxs(DetailRow, { children: [_jsx("span", { className: "label", children: "Service" }), _jsx("span", { className: "value", children: pkg.serviceType.replace(/_/g, ' ') })] }), _jsxs(DetailRow, { children: [_jsx("span", { className: "label", children: "Packaging" }), _jsx("span", { className: "value", children: pkg.packagingType.replace(/_/g, ' ') })] })] }), _jsxs("div", { children: [_jsxs(DetailRow, { children: [_jsx("span", { className: "label", children: "Weight" }), _jsxs("span", { className: "value", children: [pkg.weight, " lbs"] })] }), _jsxs(DetailRow, { children: [_jsx("span", { className: "label", children: "Declared Value" }), _jsxs("span", { className: "value", children: ["$", pkg.declaredValue.toLocaleString()] })] })] })] })] }), _jsxs(DetailSection, { style: { marginTop: '1.5rem' }, children: [_jsx("h4", { children: "Cost Estimate" }), loading ? (_jsx("p", { style: { color: 'var(--text-secondary)' }, children: "Calculating cost..." })) : error ? (_jsxs("div", { style: { color: '#ef4444' }, children: [_jsx("p", { children: error }), _jsx("button", { onClick: calculateCost, style: { color: '#ef4444', background: 'none', border: '1px solid #ef4444', borderRadius: 6, padding: '0.35rem 0.75rem', cursor: 'pointer', marginTop: '0.5rem' }, children: "Recalculate" })] })) : cost ? (_jsxs(_Fragment, { children: [_jsx(CostTable, { children: _jsxs("tbody", { children: [cost.breakdown.map((item, i) => (_jsxs("tr", { children: [_jsx("td", { children: item.title }), _jsx("td", { children: item.displayValue })] }, i))), cost.finalAmount && _jsxs("tr", { children: [_jsx("td", { children: "Total" }), _jsx("td", { children: cost.finalAmount })] })] }) }), cost.finalAmount && _jsx(TotalAmount, { children: cost.finalAmount })] })) : null] }), !isMockMode && labelEnabled && (_jsxs(DetailSection, { style: { marginTop: '1.5rem' }, children: [_jsx("h4", { children: "Generate Label" }), labelResult && (_jsxs("p", { style: { padding: '0.6rem 0.8rem', fontSize: '0.85rem', border: '1px solid', marginBottom: '0.75rem',
                            borderColor: labelResult.ok ? '#00c85355' : '#ff525255', color: labelResult.ok ? '#00c853' : '#ff5252' }, children: [labelResult.msg, labelResult.tracking ? ` — Tracking #${labelResult.tracking}` : ''] })), labelResult?.ok ? (_jsx("p", { style: { color: 'var(--text-secondary)', fontSize: '0.85rem' }, children: "Shipment saved to the Shipments tab. The label PDF opened in a new tab." })) : (_jsxs(_Fragment, { children: [_jsx("p", { style: { color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '0.75rem' }, children: "Creates a real, billable FedEx label through IFS using everything above." }), _jsx(ActionButton, { "$variant": "primary", onClick: handleGenerateLabel, disabled: generating || loading || !cost, children: generating ? 'Generating label…' : `Generate Label${cost?.finalAmount ? ` (${cost.finalAmount})` : ''}` })] }))] }))] }));
};
export default CostPreviewStep;
